import cloneDeep from 'lodash/cloneDeep';
import { domain } from 'modules/data';
import React, { FC, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';
import { ValueOf } from 'type-fest';

import { useSaveAndSubmitVendorMutation } from './hooks';

type ChangeVendorDeepProperty = <
    Path extends 'address' | 'paymentDetails',
    Prop extends keyof NonNullable<domain.QBooksVendor[Path]>,
>(
    path: Path,
    property: Prop
) => (value: ValueOf<Prop>) => void;

interface QBOVendorRequestContextType {
    vendor: domain.QBooksVendor;
    changeVendorProperty: (property: keyof domain.QBooksVendor) => (value: any) => void;
    changeVendorDeepProperty: ChangeVendorDeepProperty;
    showErrors: boolean;
    setShowErrors: (newValue: boolean) => void;
    saveAndSubmitVendor: (commentText: string, needToCompleteReview: boolean) => void;
    updateVendor: (newVendor: domain.QBooksVendor) => void;
    isSaving: boolean;
}

const QBOVendorRequestContext = React.createContext<QBOVendorRequestContextType>({
    changeVendorProperty: () => {},
} as any);

interface QBOVendorRequestContextProviderProps extends PropsWithChildren {
    request: domain.QBooksVendorRequest;
    editRequestField: <TDetails, TKey extends keyof TDetails = keyof TDetails>(
        propName: TKey,
        newValue: TDetails[TKey]
    ) => void;
}

export const QBOVendorRequestContextProvider: FC<QBOVendorRequestContextProviderProps> = (props) => {
    const { children, editRequestField, request } = props;

    const [vendor, setVendor] = useState<domain.QBooksVendor>(
        (cloneDeep(request.details) as domain.QBooksVendor) || {
            displayName: '',
            companyName: '',
            title: '',
            firstName: '',
            middleName: '',
            lastName: '',
            suffix: '',
            emailAddress: '',
            webSite: '',
            phone: '',
            mobile: '',
            fax: '',
            address: {},

            accountNumber: '',

            notes: '',
            businessNumber: '',
        }
    );
    const [showErrors, setShowErrors] = useState(false);

    const changeVendorProperty = useCallback(
        (property: keyof domain.QBooksVendor) => (value: any) => {
            editRequestField<domain.QBooksVendorDetails>(property, value);
            setVendor((vendor) => ({ ...vendor, [property]: value }));
        },
        [editRequestField]
    );

    const updateVendor = useCallback((newVendor: domain.QBooksVendorDetails) => {
        setVendor(newVendor);
    }, []);

    const changeVendorDeepProperty = useCallback<ChangeVendorDeepProperty>(
        (path, property) => (value) => {
            setVendor((vendor) => {
                const field = { ...vendor[path], [property]: value };

                editRequestField<domain.QBooksVendor>(path, field);

                return { ...vendor, [path]: field };
            });
        },
        [editRequestField]
    );

    const { saveAndSubmitVendor, isSaving } = useSaveAndSubmitVendorMutation(vendor, request);

    const value = useMemo(
        () => ({
            vendor,
            changeVendorProperty,
            showErrors,
            setShowErrors,
            saveAndSubmitVendor,
            changeVendorDeepProperty,
            updateVendor,
            isSaving,
        }),
        [
            changeVendorDeepProperty,
            changeVendorProperty,
            saveAndSubmitVendor,
            showErrors,
            vendor,
            updateVendor,
            isSaving,
        ]
    );

    return <QBOVendorRequestContext.Provider value={value}>{children}</QBOVendorRequestContext.Provider>;
};

export function useQBOVendorRequestContext() {
    return useContext(QBOVendorRequestContext);
}
